import {
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, message, Modal, Popconfirm } from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import * as React from "react";
import { TableAPI } from "../../API/TableAPI";
import { ColumnDescription } from "../../Model/Column";
import Entry from "../../Model/Entry";

import "./ShowEntryModal.css";

export interface ShowEntryModalProps {
  /** Logged in user */
  user?: {
    userId: number;
    fullName: string;
    isAdmin: boolean;
  };
  /** Entry to show */
  entry?: Entry;
  /** Column descriptions for labels etc */
  columns: Record<string, ColumnDescription>;
  /** Table API from parent */
  tableAPI: TableAPI;
  /** Is modal visible */
  visible: boolean;
  /** Callback to hide modal */
  onHide?(refresh: boolean): void;
  /** Callback to hide modal and display edit modal */
  onEdit?(): void;
}

export interface ShowEntryModalState {
  /**
   * True when waiting for a delete response
   * Disables buttons and shows spinner
   */
  isDeleting: boolean;
}

export class ShowEntryModal extends React.Component<
  ShowEntryModalProps,
  ShowEntryModalState
> {
  constructor(props: ShowEntryModalProps) {
    super(props);

    this.state = { isDeleting: false };
  }

  public render() {
    // Modal content, fill with either the entry or an error message
    const entry = this.props.entry;
    let originalCreator: number | null = null;
    let content: JSX.Element;

    if (entry === undefined) {
      content = <div>No entry to show!</div>;
      originalCreator = null;
    } else {
      // Render each one of the entries value as a label/value pair
      // and add to content
      const valueElements = Object.entries(this.props.columns)
        .filter(([key, col]) => col.visible)
        .map(([key, col]) => {
          let value: any;

          if (col.type !== "header" && entry[key] === undefined) {
            value = "N/A";
          } else {
            // Handle different data types
            switch (col.type) {
              case "header":
                value = this.renderChildren(col, entry);
                break;
              case "enum":
                value = col.options!![entry[key]];
                break;
              case "checkbox":
                value = entry[key] ? "Yes" : "No";
                break;
              case "bool-dropdown":
                value = entry[key] ? col.options!![0] : col.options!![1];
                break;
              case "mail":
              case "url":
                const url = entry[key];
                value = url ? <a href={url}>{url}</a> : "N/A";
                break;
              default:
                value = entry[key];
                break;
            }
          }

          return (
            <tr key={key}>
              <td className="label">{col.label}</td>
              <td className="value">{value}</td>
            </tr>
          );
        });

      content = (
        <table className="entry-table">
          <tbody>{valueElements}</tbody>
        </table>
      );
      originalCreator = parseInt(entry["creator"]);
    }

    // Check user (is logged in, is admin?)
    // The null casts are okay here because they are guarded by the `isLoggedIn` check
    const isLoggedIn = this.props.user !== undefined;
    const isAdmin = isLoggedIn && this.props.user!!.isAdmin;
    const isOriginalAuthor = isLoggedIn
      ? this.props.user!!.userId === originalCreator
      : false;

    // Return modal with the content we made above
    return (
      <Modal
        visible={this.props.visible}
        bodyStyle={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
        title="Entry details"
        footer={null}
        onCancel={this.onClose}
        centered={true}
      >
        {content}
        {isLoggedIn && (isOriginalAuthor || isAdmin) && (
          <div className="edit-callout">
            <span>Something is wrong? Contribute a fix!</span>
            <ButtonGroup>
              <Button
                icon={<EditOutlined />}
                onClick={this.onEdit}
                disabled={this.state.isDeleting}
              />
              {isAdmin && (
                <Popconfirm
                  title="Are you sure delete this row? This is irreversible!"
                  onConfirm={this.onDelete}
                  okText="Yes"
                  cancelText="No"
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                >
                  <Button
                    icon={<DeleteOutlined />}
                    loading={this.state.isDeleting}
                  />
                </Popconfirm>
              )}
            </ButtonGroup>
          </div>
        )}
      </Modal>
    );
  }

  private onClose = () => {
    if (this.props.onHide !== undefined) {
      this.props.onHide(false);
    }
  };

  private onDelete = async () => {
    // Try to delete, show loading
    this.setState({ isDeleting: true });

    if (this.props.user === undefined || this.props.entry === undefined) {
      return;
    }

    const result = await this.props.tableAPI.delete(this.props.entry.id);

    this.setState({ isDeleting: false });

    if (result) {
      message.success("Entry successfully deleted!");
      if (this.props.onHide !== undefined) {
        this.props.onHide(true);
      }
    } else {
      message.error("Could not delete entry!");
    }
  };

  private onEdit = () => {
    if (this.props.onEdit !== undefined) {
      this.props.onEdit();
    }
  };

  /**
   * Returns the value of a column with children as a string for rendering
   */
  private renderChildren = (col: ColumnDescription, entry: Entry) => {
    const children = col.children;
    if (children === undefined || Object.keys(children).length === 0) {
      console.error(
        "[ShowEntryModal.renderChildren] Asked to render a column's children but the given column has no children!"
      );
      console.error(col);
      return "";
    }

    return Object.entries(children)
      .map(([key, child]) => {
        switch (child.type) {
          case "month":
            return entry[key].substring(0, 7);
          case "date-present":
            return entry[key] ? entry[key] : "Present";
          default:
            return entry[key] ? entry[key] : "N/A";
        }
      })
      .join(" - ");
  };
}
